import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * NotificationDrawerContext — shell-local open/close controller for
 * the global notification drawer.
 *
 * Kept intentionally small: `{ isOpen, open, close, toggle }`. No
 * data lives here — the drawer's list content is pulled through the
 * existing `useUnreadNotifications` hook (and any future notification
 * store) so this context doesn't turn into a second-tier state
 * library.
 *
 * `openerRef` records the element that had focus at open time so
 * closing can return focus to the bell (or wherever the user tapped
 * from). We store it as a ref not state so re-renders don't recreate
 * the reference and confuse focus mgmt.
 */
const NotificationDrawerContext = createContext(null);

export function NotificationDrawerProvider({ children }) {
    const [isOpen, setIsOpen] = useState(false);
    const openerRef = useRef(null);

    const open = useCallback(() => {
        if (typeof document !== 'undefined') {
            const active = document.activeElement;
            // Only record it if it's an actual focusable — skip the
            // body/root fallback so we don't try to `focus()` those on
            // close (which would silently swallow focus).
            openerRef.current = (active && active !== document.body) ? active : null;
        }
        setIsOpen(true);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    // After a close, hand focus back to the recorded opener. Done in
    // an effect so React has re-rendered the tree (panel unmounted)
    // before we try to focus outside it.
    useEffect(() => {
        if (isOpen) return;
        const opener = openerRef.current;
        openerRef.current = null;
        if (opener && typeof opener.focus === 'function') {
            // rAF instead of setTimeout so we hand focus back at the
            // very next paint — no perceptible delay.
            const raf = (typeof window !== 'undefined' && window.requestAnimationFrame)
                ? window.requestAnimationFrame
                : (cb) => setTimeout(cb, 0);
            raf(() => opener.focus());
        }
    }, [isOpen]);

    const toggle = useCallback(() => {
        setIsOpen((prev) => {
            if (!prev && typeof document !== 'undefined') {
                const active = document.activeElement;
                openerRef.current = (active && active !== document.body) ? active : null;
            }
            return !prev;
        });
    }, []);

    const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);

    return (
        <NotificationDrawerContext.Provider value={value}>
            {children}
        </NotificationDrawerContext.Provider>
    );
}

/**
 * Hook — throws if used outside the provider so a stray call from a
 * detached test can't silently no-op.
 */
export function useNotificationDrawer() {
    const ctx = useContext(NotificationDrawerContext);
    if (!ctx) {
        throw new Error('useNotificationDrawer must be used within <NotificationDrawerProvider>');
    }
    return ctx;
}

/**
 * Safer variant for optional consumers (e.g. a component that MAY be
 * mounted outside the shell in a test). Returns a no-op stub instead
 * of throwing.
 */
export function useOptionalNotificationDrawer() {
    const ctx = useContext(NotificationDrawerContext);
    return ctx || { isOpen: false, open: () => {}, close: () => {}, toggle: () => {} };
}
